import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, X, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

const CORE_DIAMETERS = [
  { value: '25', label: '25mm' },
  { value: '38', label: '38mm' },
  { value: '76', label: '76mm' },
  { value: '152', label: '152mm' },
  { value: 'other', label: 'Other' }
];

const CERTIFICATIONS = [
  { value: 'FSC', label: 'FSC' },
  { value: 'ISCC', label: 'ISCC' },
  { value: 'BRC', label: 'BRC' },
  { value: 'Halal', label: 'Halal' },
  { value: 'Kosher', label: 'Kosher' }
];

const AGE_BUCKETS = [
  { value: '0_90', label: '< 3 months' },
  { value: '91_180', label: '3-6 months' },
  { value: '181_360', label: '6-12 months' },
  { value: 'gt_360', label: '> 12 months' }
];

const QUANTITY_UNITS = [
  { value: 'sqm', label: 'sqm' },
  { value: 'kg', label: 'kg' },
  { value: 'rolls', label: 'rolls' }
];

export const AdvancedFilterSidebar = ({ 
  filters, 
  onFilterChange, 
  onClearFilters,
  className 
}) => {
  const { t } = useTranslation();
  
  // Section collapse states
  const [technicalOpen, setTechnicalOpen] = useState(true);
  const [propertiesOpen, setPropertiesOpen] = useState(true);
  const [stockOpen, setStockOpen] = useState(true);

  // Local state for debounced inputs
  const [localFilters, setLocalFilters] = useState({
    min_width: filters.min_width || '',
    max_width: filters.max_width || '',
    min_thickness: filters.min_thickness || '',
    max_thickness: filters.max_thickness || '',
    min_quantity: filters.min_quantity || ''
  });

  // Sync local state when filters change externally
  useEffect(() => {
    setLocalFilters({
      min_width: filters.min_width || '',
      max_width: filters.max_width || '',
      min_thickness: filters.min_thickness || '',
      max_thickness: filters.max_thickness || '',
      min_quantity: filters.min_quantity || ''
    });
  }, [filters.min_width, filters.max_width, filters.min_thickness, filters.max_thickness, filters.min_quantity]);

  // Debounced update
  const debouncedUpdate = useCallback((key, value) => {
    const timeoutId = setTimeout(() => {
      onFilterChange({ [key]: value });
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [onFilterChange]);

  // Handle number input changes with debounce
  const handleNumberChange = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
    debouncedUpdate(key, value);
  };

  // Handle multi-select checkbox changes
  const handleMultiSelectChange = (filterKey, value, checked) => {
    const currentValues = filters[filterKey] ? filters[filterKey].split(',').filter(Boolean) : [];
    let newValues;
    if (checked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(v => v !== value);
    }
    onFilterChange({ [filterKey]: newValues.join(',') });
  };

  // Check if a value is in a comma-separated filter string
  const isInFilter = (filterKey, value) => {
    const currentValues = filters[filterKey] ? filters[filterKey].split(',') : [];
    return currentValues.includes(value);
  };

  // Handle customer specific toggle (3-state: yes/no/both)
  const handleCustomerSpecificChange = (value) => {
    onFilterChange({ customer_specific: value === 'both' ? '' : value });
  };

  // Calculate active filter count for each section
  const getTechnicalFilterCount = () => {
    let count = 0;
    if (filters.min_width || filters.max_width) count++;
    if (filters.min_thickness || filters.max_thickness) count++;
    if (filters.core_diameters) count++;
    return count;
  };

  const getPropertiesFilterCount = () => {
    let count = 0;
    if (filters.certifications) count++;
    if (filters.customer_specific) count++;
    if (filters.compliance_flag !== undefined && filters.compliance_flag !== '') count++;
    return count;
  };

  const getStockFilterCount = () => {
    let count = 0;
    if (filters.min_quantity) count++;
    if (filters.age_buckets) count++;
    return count;
  };

  const totalFilterCount = getTechnicalFilterCount() + getPropertiesFilterCount() + getStockFilterCount();

  const FilterSection = ({ title, isOpen, onToggle, filterCount, children }) => (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <CollapsibleTrigger asChild>
        <button 
          className="flex items-center justify-between w-full py-3 px-1 text-sm font-semibold text-foreground hover:text-primary transition-colors border-b border-border"
          data-testid={`filter-section-${title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <span className="flex items-center gap-2">
            {title}
            {filterCount > 0 && (
              <Badge variant="secondary" className="h-5 min-w-5 px-1.5 text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400">
                {filterCount}
              </Badge>
            )}
          </span>
          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-4 pb-2 space-y-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );

  return (
    <div className={cn("space-y-2", className)} data-testid="advanced-filter-sidebar">
      {/* Header */}
      <div className="flex items-center justify-between pb-2">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Filter className="h-4 w-4" />
          {t('filter.advancedFilters', 'Advanced Filters')}
        </h3>
        {totalFilterCount > 0 && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={onClearFilters}
            className="h-7 text-xs text-muted-foreground hover:text-foreground"
            data-testid="clear-advanced-filters"
          >
            <X className="h-3 w-3 mr-1" />
            {t('filter.clearAll', 'Clear')} ({totalFilterCount})
          </Button>
        )}
      </div>

      {/* Section 1: Technical Specifications */}
      <FilterSection 
        title={t('filter.technicalSpecs', 'Technical Specifications')} 
        isOpen={technicalOpen} 
        onToggle={setTechnicalOpen}
        filterCount={getTechnicalFilterCount()}
      >
        {/* Width Range */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{t('filter.widthRange', 'Width Range')} (mm)</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder={t('filter.min', 'Min')}
              value={localFilters.min_width}
              onChange={(e) => handleNumberChange('min_width', e.target.value)}
              className="h-8 text-sm"
              data-testid="filter-min-width"
            />
            <Input
              type="number"
              placeholder={t('filter.max', 'Max')}
              value={localFilters.max_width}
              onChange={(e) => handleNumberChange('max_width', e.target.value)}
              className="h-8 text-sm"
              data-testid="filter-max-width"
            />
          </div>
        </div>

        {/* Thickness Range */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{t('filter.thicknessRange', 'Thickness Range')} (µm)</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder={t('filter.min', 'Min')}
              value={localFilters.min_thickness}
              onChange={(e) => handleNumberChange('min_thickness', e.target.value)}
              className="h-8 text-sm"
              data-testid="filter-min-thickness"
            />
            <Input
              type="number"
              placeholder={t('filter.max', 'Max')}
              value={localFilters.max_thickness}
              onChange={(e) => handleNumberChange('max_thickness', e.target.value)}
              className="h-8 text-sm"
              data-testid="filter-max-thickness"
            />
          </div>
        </div>

        {/* Core Diameter */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{t('filter.coreDiameter', 'Core Diameter')}</Label>
          <div className="flex flex-wrap gap-2">
            {CORE_DIAMETERS.map((core) => (
              <label 
                key={core.value} 
                className="flex items-center gap-1.5 text-sm cursor-pointer"
              >
                <Checkbox
                  checked={isInFilter('core_diameters', core.value)}
                  onCheckedChange={(checked) => handleMultiSelectChange('core_diameters', core.value, checked)}
                  data-testid={`filter-core-${core.value}`}
                />
                <span>{core.label}</span>
              </label>
            ))}
          </div>
        </div>
      </FilterSection>

      {/* Section 2: Material Properties */}
      <FilterSection 
        title={t('filter.materialProperties', 'Material Properties')} 
        isOpen={propertiesOpen} 
        onToggle={setPropertiesOpen}
        filterCount={getPropertiesFilterCount()}
      >
        {/* Certifications */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{t('filter.certifications', 'Certifications')}</Label>
          <div className="flex flex-wrap gap-2">
            {CERTIFICATIONS.map((cert) => (
              <label 
                key={cert.value} 
                className="flex items-center gap-1.5 text-sm cursor-pointer"
              >
                <Checkbox
                  checked={isInFilter('certifications', cert.value)}
                  onCheckedChange={(checked) => handleMultiSelectChange('certifications', cert.value, checked)}
                  data-testid={`filter-cert-${cert.value.toLowerCase()}`}
                />
                <span>{cert.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Customer Specific */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{t('filter.customerSpecific', 'Customer Specific')}</Label>
          <Select
            value={filters.customer_specific || 'both'}
            onValueChange={handleCustomerSpecificChange}
          >
            <SelectTrigger className="h-8 text-sm" data-testid="filter-customer-specific">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="both">{t('filter.both', 'Both')}</SelectItem>
              <SelectItem value="yes">{t('filter.yes', 'Yes')}</SelectItem>
              <SelectItem value="no">{t('filter.no', 'No')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Compliance Flag */}
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">{t('filter.complianceFlag', 'Compliance Flag')}</Label>
          <Switch
            checked={filters.compliance_flag === true}
            onCheckedChange={(checked) => onFilterChange({ compliance_flag: checked ? true : '' })}
            data-testid="filter-compliance-flag"
          />
        </div>
      </FilterSection>

      {/* Section 3: Stock Details */}
      <FilterSection 
        title={t('filter.stockDetails', 'Stock Details')} 
        isOpen={stockOpen} 
        onToggle={setStockOpen}
        filterCount={getStockFilterCount()}
      >
        {/* Minimum Quantity */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{t('filter.minQuantity', 'Minimum Quantity')}</Label>
          <div className="flex gap-2">
            <Input
              type="number"
              placeholder={t('filter.quantity', 'Quantity')}
              value={localFilters.min_quantity}
              onChange={(e) => handleNumberChange('min_quantity', e.target.value)}
              className="h-8 text-sm flex-1"
              data-testid="filter-min-quantity"
            />
            <Select
              value={filters.quantity_unit || 'sqm'}
              onValueChange={(value) => onFilterChange({ quantity_unit: value })}
            >
              <SelectTrigger className="h-8 w-24 text-sm" data-testid="filter-quantity-unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUANTITY_UNITS.map((unit) => (
                  <SelectItem key={unit.value} value={unit.value}>{unit.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Age Bucket */}
        <div className="space-y-2">
          <Label className="text-xs text-muted-foreground">{t('filter.ageBucket', 'Stock Age')}</Label>
          <div className="space-y-2">
            {AGE_BUCKETS.map((bucket) => (
              <label 
                key={bucket.value} 
                className="flex items-center gap-2 text-sm cursor-pointer"
              >
                <Checkbox
                  checked={isInFilter('age_buckets', bucket.value)}
                  onCheckedChange={(checked) => handleMultiSelectChange('age_buckets', bucket.value, checked)}
                  data-testid={`filter-age-${bucket.value}`}
                />
                <span>{t(`filter.age_${bucket.value}`, bucket.label)}</span>
              </label>
            ))}
          </div>
        </div>
      </FilterSection>
    </div>
  );
};

export default AdvancedFilterSidebar;
